<template>
  <div
    v-if="title"
    :class="{ 'is-open': isOpen }"
    class="accordion-item"
  >
    <button
      class="accordion-item__header"
      :aria-expanded="isOpen"
      @click="toggleItem"
    >
      <span
        class="accordion-item__title"
        :style="{ opacity: isOpen ? 0 : 1 }"
        v-text="title"
      />
      <span class="accordion-item__icon">
        <icon-down />
      </span>
    </button>

    <transition
      name="accordion"
      @before-enter="beforeEnter"
      @enter="enter"
      @before-leave="beforeLeave"
      @leave="leave"
    >
      <div
        v-show="isOpen"
        :aria-hidden="!isOpen"
        class="accordion-item__inner"
      >
        <button
          class="accordion-item__title"
          :style="{ opacity: isOpen ? 1 : 0 }"
          @click="toggleItem"
          v-text="title"
        />
        <slot name="content" />

        <div
          v-if="html"
          class="accordion-item__html"
          v-html="html"
        />
      </div>
    </transition>
  </div>
</template>
<script>

/**
 * Component: Accordion Item
 * -----------------------------------------------------------------------------
 * @component Accordion Item that accepts a slot named "content".
 *
 * @param {Object} [title] - Accordion title.
 * @param {String} [html] - HTML content pasted via textarea to render generated tables.
 *
 * @example
 * <accordion-item :title="titleProp">
 *   <template v-slot:content>
 *     <!-- Your content here -->
 *   </template>
 * </accordion-item>
 */

import IconDown from '~icons/direction/down.svg'

export default {
  name: 'AccordionItem',

  components: {
    IconDown,
  },

  props: {
    title: {
      type: String,
      default: '',
    },
    html: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isOpen: false,
    }
  },

  methods: {

    /**
     * Toggle an accordion tab visibility.
     */
    toggleItem() {
      this.isOpen = !this.isOpen
    },

    /**
     * Event handler called before the element enters the DOM during a Vue transition.
     * Sets the initial height of the element to 0.
     *
     * @param {HTMLElement} el - The element being transitioned.
     */
    beforeEnter(el) {
      el.style.maxHeight = '0'
    },

    /**
     * Event handler called when the element enters the DOM during a Vue transition.
     * Sets the height of the element to its calculated scroll height to animate
     * its expansion.
     *
     * @param {HTMLElement} el - The element being transitioned.
     */
    enter(el) {
      el.style.maxHeight = `${el.scrollHeight}px`
    },

    /**
     * Event handler called before the element leaves the DOM during a Vue transition.
     * Sets the height of the element to its calculated scroll height to animate
     * its collapse.
     *
     * @param {HTMLElement} el - The element being transitioned.
     */
    beforeLeave(el) {
      el.style.maxHeight = `${el.scrollHeight}px`
    },

    /**
     * Event handler called when the element leaves the DOM during a Vue transition.
     * Sets the height of the element to 0 to animate its collapse.
     *
     * @param {HTMLElement} el - The element being transitioned.
     */
    leave(el) {
      el.style.maxHeight = '0'
    },

  },
}
</script>

<style src="./accordion-item.scss" lang="scss"></style>
